import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { NOT_VALID, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { isEqual } from 'lodash-es';
import { toNum } from '@shared/helpers/math.helper';
import { isValidDayMonth } from '@shared/helpers/date.helper';
import { getBaseSocialeControl } from './helpers/base-sociale-form.helper';

export const baseSocialeEserciziPrecedentiRules: FormGroupRules = {
    my_Data1EsPrec: (form: FormGroup) => {
        dataEsPecHelper(form, 'my_Data1EsPrec', 'my_DataApprovazione2', 'my_TipoSchemaBil2');
    },

    my_Data2EsPrec: (form: FormGroup) => {
        dataEsPecHelper(form, 'my_Data2EsPrec', 'my_DataApprovazione1', 'my_TipoSchemaBil1');
    },

    my_UnitaSociUtilPrimo: (form: FormGroup) => {
        unitaSociUtilHelper(form, 'my_UnitaSociUtilPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo', 'Primo');
    },

    my_UnitaNonSociUtilPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_UnitaNonSociUtilPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_NumLavSubPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavSubPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_NumLavSubTempoDeterminatoPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavSubTempoDeterminatoPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_NumLavParaSubPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavParaSubPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_NumLavAutPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavAutPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_NumLavAltroPrimo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavAltroPrimo', 'my_Data1EsPrec', 'my_ObbligoRilevazioneLavPrimo');
    },

    my_UnitaSociUtilSecondo: (form: FormGroup) => {
        unitaSociUtilHelper(form, 'my_UnitaSociUtilSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo', 'Secondo');
    },

    my_UnitaNonSociUtilSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_UnitaNonSociUtilSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_NumLavSubSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavSubSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_NumLavSubTempoDeterminatoSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavSubTempoDeterminatoSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_NumLavParaSubSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavParaSubSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_NumLavAutSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavAutSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_NumLavAltroSecondo: (form: FormGroup) => {
        obbligoRilevazioneHelper(form, 'my_NumLavAltroSecondo', 'my_Data2EsPrec', 'my_ObbligoRilevazioneLavSecondo');
    },

    my_ULAPrimo: (form: FormGroup) => {
        ulaHelper(form, 'my_ULAPrimo', 'my_BilAnno1');
    },

    my_ULASecondo: (form: FormGroup) => {
        ulaHelper(form, 'my_ULASecondo', 'my_BilAnno2');
    },

    my_ChiusuraEsercizio: (form: FormGroup) => {
        const control = getBaseSocialeControl(form, 'my_ChiusuraEsercizio');
        const value: string = control.value ?? '';
        const errors = control.errors ? control.errors : {};
        const isInvalidFormat = !isNilOrEmpty(value) && (value.length !== 5 || (value[2] !== '-' && value[2] !== '/') || !isValidDayMonth(value));
        addRemoveError(isInvalidFormat, errors, 'my_ChiusuraEsercizio', { status: NOT_VALID });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const dataEsPecHelper = (form: FormGroup, targetPath: string, dataApprovazionePath: string, tipoSchemaPath: string) => {
    const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
    const targetControl = getBaseSocialeControl(form, targetPath);
    const my_DataApprovazione: string = getControl(datiBilancioGroup, dataApprovazionePath)?.value;
    const my_TipoSchemaBil: string = getControl(datiBilancioGroup, tipoSchemaPath)?.value;
    const my_BilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;
    const errors = targetControl.errors ? targetControl.errors : {};
    const showError = isNilOrEmpty(targetControl?.value) && !isNilOrEmpty(my_DataApprovazione) && !isNilOrEmpty(my_TipoSchemaBil) && !isNilOrEmpty(my_BilancioAllegato);

    addRemoveError(showError, errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

const unitaSociUtilHelper = (form: FormGroup, targetPath: string, dataPath: string, obbligoPath: string, suffix: 'Primo' | 'Secondo') => {
    const buildPath = (name: string) => {
        return `${name}${suffix}`;
    };
    obbligoRilevazioneHelper(form, targetPath, dataPath, obbligoPath);
    const my_UnitaSociUtilControl = getBaseSocialeControl(form, targetPath);
    const my_UnitaSociUtil = toNum(my_UnitaSociUtilControl?.value);
    const my_UnitaNonSociUtil = toNum(getBaseSocialeControl(form, buildPath('my_UnitaNonSociUtil'))?.value);
    const my_NumLavSub = toNum(getBaseSocialeControl(form, buildPath('my_NumLavSub'))?.value);
    const my_NumLavSubTempoDeterminato = toNum(getBaseSocialeControl(form, buildPath('my_NumLavSubTempoDeterminato'))?.value);
    const my_NumLavParaSub = toNum(getBaseSocialeControl(form, buildPath('my_NumLavParaSub'))?.value);
    const my_NumLavAut = toNum(getBaseSocialeControl(form, buildPath('my_NumLavAut'))?.value);
    const my_NumLavAltro = toNum(getBaseSocialeControl(form, buildPath('my_NumLavAltro'))?.value);
    const my_DataEsPrec: string = getBaseSocialeControl(form, dataPath)?.value;
    const my_ObbligoRilevazioneLav: string = getBaseSocialeControl(form, obbligoPath)?.value;
    const showError =
        !isNilOrEmpty(my_DataEsPrec) &&
        !(my_UnitaNonSociUtil + toNum(my_UnitaSociUtil) === my_NumLavSub + my_NumLavSubTempoDeterminato + my_NumLavParaSub + my_NumLavAut + my_NumLavAltro) &&
        my_ObbligoRilevazioneLav === 'S';
    const errors = my_UnitaSociUtilControl.errors ? my_UnitaSociUtilControl.errors : {};

    addRemoveError(showError, errors, targetPath, {
        status: 'Totali non congruenti',
    });

    my_UnitaSociUtilControl.setErrors(isEqual(errors, {}) ? null : errors);
};

const ulaHelper = (form: FormGroup, ulaPath: string, bilAnnoPath: string) => {
    const my_ULAControl = getBaseSocialeControl(form, ulaPath);
    const my_ULA = my_ULAControl?.value;
    const my_BilAnno = toNum(getControl(form, `${formPaths.datiBilancio}.${bilAnnoPath}`)?.value);
    const errors = my_ULAControl.errors ? my_ULAControl.errors : {};

    addRemoveError(isNilOrEmpty(my_ULA) && !isNilOrEmpty(my_BilAnno) && my_BilAnno > 2020, errors, ulaPath, {
        status: REQUIRED_STATUS,
    });

    my_ULAControl.setErrors(isEqual(errors, {}) ? null : errors);
};

const obbligoRilevazioneHelper = (form: FormGroup, targetPath: string, dataPath: string, obbligoPath: string) => {
    const targetControl = getBaseSocialeControl(form, targetPath);
    const my_Data: string = getBaseSocialeControl(form, dataPath)?.value;
    const obbligo: string = getBaseSocialeControl(form, obbligoPath)?.value;
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(!isNilOrEmpty(my_Data) && isNilOrEmpty(targetControl?.value) && obbligo === 'S', errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
